/**
 * Status premium client — dibaca dari field `premium` milik server
 * (GET /v1/billing/wallet), di-cache per akun di localStorage.
 *
 * Pola mengikuti lib/auth.ts: sync read untuk render, refresh async
 * setelah mount. Cache TIDAK dipercaya untuk keamanan — server selalu
 * menegaskan entitlement di setiap endpoint (403 PremiumRequired).
 */

import { getWallet } from './api/billing';
import { getStoredAccount, isGuestSession } from './auth';

const STORAGE_KEY = 'funpaper.premium.v1';

export type PremiumStatus = {
  isPremium: boolean;
  accountId: number | null;
  updatedAt: string;
};

function readCache(): PremiumStatus | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = (JSON.parse(raw) as Partial<PremiumStatus>);
    if (typeof parsed.isPremium !== 'boolean') return null;
    return {
      isPremium: parsed.isPremium,
      accountId: typeof parsed.accountId === 'number' ? parsed.accountId : null,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : '',
    };
  } catch {
    return null;
  }
}

function writeCache(status: PremiumStatus): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(status));
  } catch {
    // localStorage disabled — status hanya hidup di memori sesi ini.
  }
}

/** Sync read — aman dipakai saat render. Default false (non-premium). */
export function getCachedPremiumStatus(): PremiumStatus | null {
  const cached = readCache();
  const account = getStoredAccount();
  if (!cached || (account && cached.accountId !== account.id)) return null;
  if (isGuestSession()) return { isPremium: false, accountId: account?.id ?? null, updatedAt: cached.updatedAt };
  return cached;
}

export function isPremiumCached(): boolean {
  return getCachedPremiumStatus()?.isPremium === true;
}

/**
 * Refresh dari server (GET wallet). Gagal jaringan = pertahankan cache lama.
 * Return status premium terbaru (false bila guest / tanpa sesi / error).
 */
export async function refreshPremiumStatus(): Promise<boolean> {
  const account = getStoredAccount();
  if (!account || isGuestSession()) {
    writeCache({ isPremium: false, accountId: account?.id ?? null, updatedAt: new Date().toISOString() });
    return false;
  }
  try {
    const wallet = await getWallet();
    writeCache({ isPremium: wallet.premium, accountId: account.id, updatedAt: new Date().toISOString() });
    return wallet.premium;
  } catch {
    return readCache()?.isPremium ?? false;
  }
}

/** Untuk tests: tulis cache langsung tanpa menyentuh jaringan. */
export function __setPremiumCacheForTests(status: PremiumStatus): void {
  writeCache(status);
}
